import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { filter, isEqual } from 'lodash-es'
import { v4 } from 'uuid'
import { Stack } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import { HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form, Errors } from '@ui/forms'
import LineGeometryFormFields from '@ui/forms/LineGeometryForm/formFields'
import { useModelStore } from '@editorStores'
import { PurlinGeometryType } from '..'
import {
  xyCartesianHorizontalPurlinSchema,
  xyDiagonalHorizontalPurlinSchema,
  xyCartesianZElevatedPurlinSchema,
  xyDiagonalZElevatedPurlinSchema,
  DefaultValues,
} from '../schemas'

interface Props {
  handleSubmit: (purlin: PlanLineElement) => void
  handleDelete: () => void
  purlin: PlanLineElement
  geometryType: PurlinGeometryType
  interactionMode: '2D' | '3D'
  isDeleting?: boolean
  isSubmitting?: boolean
  showCreateLabel?: boolean
  geometryTypeToggle?: ReactNode
}

const GeometryForm = ({
  purlin,
  handleSubmit,
  handleDelete,
  geometryType,
  interactionMode,
  isDeleting = false,
  isSubmitting,
  showCreateLabel = false,
  geometryTypeToggle,
}: Props): ReactElement | null => {
  const { purlins } = useModelStore(state => state.model)
  const updatePurlin = useModelStore(state => state.updatePurlin)

  const purlinsInStorey = useMemo(() => {
    return filter(purlins, ['storey', purlin.storey])
  }, [purlin.storey, purlins])

  const defaultValues = useMemo<DefaultValues>(() => {
    const { start } = purlin.shape
    const { end } = purlin.shape

    return {
      guid: purlin.guid,
      start,
      end,
      isIntersecting: false,
      ...(showCreateLabel ? { label: '' } : {}),
    }
  }, [purlin, showCreateLabel])

  const validationSchema = useMemo(() => {
    const geometrySchema = (() => {
      switch (geometryType) {
        case 'x-or-y-no-z-elevation':
          return xyCartesianHorizontalPurlinSchema
        case 'xy-diagonal-no-z-elevation':
          return xyDiagonalHorizontalPurlinSchema
        case 'x-or-y-z-elevation':
          return xyCartesianZElevatedPurlinSchema
        case 'xy-diagonal-z-elevation':
          return xyDiagonalZElevatedPurlinSchema
        default:
          return xyCartesianHorizontalPurlinSchema
      }
    })() as typeof xyCartesianHorizontalPurlinSchema
    return showCreateLabel ? geometrySchema.concat(createLabelSchema) : geometrySchema
  }, [geometryType, showCreateLabel])

  const validationContext = {
    purlins: purlinsInStorey,
  }

  // this is necessary so that the form state is reset when the form is submitted
  const [formStateRandomId, setFormStateRandomId] = useState(v4())

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton

  return (
    <Form
      key={`${purlin.guid}-${formStateRandomId}`}
      onSubmit={state => {
        handleSubmit(showCreateLabel ? applyCreateLabel(purlin, state.label) : purlin)
        setFormStateRandomId(v4())
      }}
      validationSchema={validationSchema}
      validationContext={validationContext}
      defaultValues={defaultValues}
      data-cy="purlin-geometry-form"
      style={{ width: '100%' }}
    >
      <FormValueUpdater purlin={purlin} />
      <Stack direction="column" spacing={1}>
        {showCreateLabel && <CreateElementLabelField />}
        {geometryTypeToggle}
        <LineGeometryFormFields
          updateElementMutation={updatePurlin}
          interactionMode={interactionMode}
        />
      </Stack>

      <Errors />

      <Stack direction="row" justifyContent="end" spacing={1} mt={1}>
        <SubmitButton
          type="submit"
          loading={isSubmitting}
          fullWidth
          disabled={isDeleting}
          data-cy="btn-submit"
        >
          Speichern
        </SubmitButton>
        <HotkeyDeleteButton
          onClick={handleDelete}
          size="small"
          fullWidth
          loading={isDeleting}
          disabled={isSubmitting}
          data-cy="geometry-form-delete-btn"
        >
          Löschen
        </HotkeyDeleteButton>
      </Stack>
    </Form>
  )
}

const FormValueUpdater = ({ purlin }: { purlin: ShapeObjectLine }): null => {
  const { setValue, getValues } = useFormContext()

  useEffect(() => {
    const formValues = getValues()

    const { start, end } = purlin.shape

    const shapeStart = { x: start.x, y: start.y, z: start.z }
    const shapeEnd = { x: end.x, y: end.y, z: end.z }

    const formStart = formValues.start
    const formEnd = formValues.end

    if (isEqual(shapeStart, formStart) && isEqual(shapeEnd, formEnd)) return

    setValue('start', shapeStart, { shouldDirty: false })
    setValue('end', shapeEnd, { shouldDirty: false })
  }, [purlin.shape, setValue, getValues])

  return null
}

export default GeometryForm
